<template>
  <vx-card class="mt-base" :title="activityTitle">
    <p class="mb-5">
      Video games are audio-visual experiences. Music is a tool used in video games to evoke
      emotions, set the tone, and enhance a scene’s atmosphere.
    </p>
    <p class="mb-5">
      Find out more about how music is used in video games in this interesting article on the BBC
      Bitesize website.
    </p>

    <a href="https://www.bbc.co.uk/bitesize/topics/z3dqhyc/articles/zdvt6v4" target="_blank">
      <centered-image-wrapper
        alt="Read the BBC Bitesize article on theme music (opens in a new tab)"
        :image="image1"
      />
    </a>

    <p class="mb-5">
      There’s also a fascinating interview with Ninichi, a video game composer, discussing how she
      approaches composing music for various video games. Here’s the interview transcript, but you
      can watch the interview in the embedded video in the link above.
    </p>

    <div class="highlight-text">
      <p class="mb-5"><strong>Sarah:</strong> Hi, Nanichi.</p>
      <p class="mb-5"><strong>Ninichi:</strong> Hi, Sarah.</p>
      <p class="mb-5"><strong>Sarah:</strong> How is music used in video games?</p>
      <p class="mb-5">
        <strong>Ninichi:</strong> One way is to use music to set the mood and to set the tone in a
        game. So, this is an example of a happy, uplifting track that I did for an empire building
        game where you essentially run a hospital.
      </p>
      <p class="mb-5 text-bold">UPBEAT HAPPY MUSIC</p>
      <p class="mb-5">
        <strong>Ninichi:</strong> And so, the general idea is to just make people happy and feel
        comfortable in this environment. Another example would be this other game where the world
        has basically ended and so the track here is quite mysterious.
      </p>
      <p class="mb-5 text-bold">DARK MYSTERIOUS MUSIC</p>
      <p class="mb-5">
        <strong>Ninichi:</strong> Another way that music is used in games is to help set the scene.
        So, different games tend to be set in different environments and in different locations,
        such as a beach…
      </p>
      <p class="mb-5 text-bold">RELAXED BEACHY MUSIC</p>
      <p class="mb-5"><strong>Sarah:</strong> It’s tropical.</p>
      <p class="mb-5">
        <strong>Ninichi:</strong> And then this. Actually, why don’t you see if you can figure out
        what this one is.
      </p>
      <p class="mb-5"><strong>Sarah:</strong> Okay.</p>
      <p class="mb-5 text-bold">MUSIC WITH BUBBLES</p>
      <p class="mb-5"><strong>Sarah:</strong> It’s definitely somewhere magical.</p>
      <p class="mb-5">
        <strong>Ninichi:</strong> Yeah. So that one is set in a witch’s hut. Music is sometimes used
        to emphasise key moments in a game. Sometimes you may start off with slightly more chilled
        out music where you’re just exploring and you’re on a general level, trying to win points
        and so on. But then at another point, you may come across a big, bad boss. In which case,
        the music will intensify, it will change, it will feel more difficult. There are so many
        different types of games. For example, role playing games. The music is there usually to
        support the story, so it’s all about storytelling and character building. So each character
        may have a different theme. This is a warrior king, so it’s quite strong and fierce.
      </p>
      <p class="mb-5 text-bold">INTENSE MUSIC</p>
      <p class="mb-5">
        <strong>Sarah:</strong> Yeah, sounds powerful. How do you get started on a project?
      </p>
      <p class="mb-5">
        <strong>Ninichi:</strong> I’ll work with the game developer to try and understand what it is
        that they really want for their game. What sort of style they have in mind, what sort of
        mood they want to set. I will also play the game, if they do have any bits that I can play.
      </p>
      <p class="mb-5">
        <strong>Sarah:</strong> So, what are some of the tools that you use when you’re composing
        your music?
      </p>
      <p class="mb-5">
        <strong>Ninichi:</strong> Well, the brilliant thing about composing music today is that you
        can really do it anywhere. So, I use my laptop to create music and then on there, I
        basically have these different tools that let me score the music, so write it out and then
        also choose lots of different sounds to pull together to create the tracks with the video
        games.
      </p>
      <p class="mb-5">
        <strong>Sarah:</strong> Have you got any tips or advice for someone who might want to get
        started in game composition?
      </p>
      <p class="mb-5">
        <strong>Ninichi:</strong> I would say try and have fun with it. Grab as many games as you
        can, play them, enjoy them but also start noticing what the music is like. And then what you
        can do is play the game without any music and then have a go at creating your own version of
        the soundtrack.
      </p>
    </div>
    <h4 class="my-5">Your Task: Source Your Own Score</h4>
    <p class="mb-5">
      Now it’s time to find some suitable sounds and music to accompany your storyboard. Use the
      following website to find some sound effects to go along with each of your storyboard frames
      from the task above and also a sample of some music that might work as the overall theme music
      for your game.
    </p>

    <p class="mb-5 highlight-text">
      To assist you with this task, you will see two buttons below. The one on the left opens the
      “FreeSound” website and the one on the right plays a short video that steps you through how to
      use it and what to do.
    </p>

    <div class="vx-row mt-10 mb-5 flex items-center justify-center">
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full mb-5">
        <a href="https://freesound.org/" target="_blank">
          <img
            :src="image2"
            alt="Open Freesound (opens in a new tab)"
            class="img-responsive mx-auto"
            style="max-height: 400px"
          />
        </a>
      </div>
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full mb-5">
        <button
          type="button"
          class="no-style img-click-button"
          aria-label="Play video: Using Freesound"
          @click="showVideoPopup(videoPopup)"
        >
          <img
            :src="image3"
            class="img-responsive mx-auto cursor-pointer"
            style="max-height: 400px"
            alt=""
          />
        </button>
      </div>
    </div>

    <p class="mb-5 text-bold">Upload your 6 sound effects and one music track:</p>
    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_' + index]"
      :is-editable="isEditable"
      :index="index"
      filetype="audio"
      v-for="index in 6"
      :key="'file_' + index"
      upload-btn-text="Upload Sound Effect"
    />

    <br />
    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_7']"
      :is-editable="isEditable"
      index="7"
      filetype="audio"
      upload-btn-text="Upload Music Track"
    />

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
export default {
  data() {
    return {
      image1: require('@/assets/images/experiences/gamesdeveloper/bbc_article_button.png'),
      image2: require('@/assets/images/experiences/gamesdeveloper/freesound_button.png'),
      image3: require('@/assets/images/experiences/gamesdeveloper/freesound_trigger.jpg'),
      uploaded_files: {
        uploaded_file_1: '',
        uploaded_file_2: '',
        uploaded_file_3: '',
        uploaded_file_4: '',
        uploaded_file_5: '',
        uploaded_file_6: '',
        uploaded_file_7: '',
      },
      videoPopup:
        $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL +
        '/content/gamesdeveloper/freesound_1000.mp4',
    };
  },
  methods: methods,
  computed: computed,
  inject: inject,
  mounted() {
    if (typeof this.uploaded_files !== 'undefined') {
      for (let i = 1; i <= Object.keys(this.uploaded_files).length; i++) {
        if (typeof this.data['uploaded_file_' + i] !== 'undefined') {
          this.uploaded_files['uploaded_file_' + i] = this.data['uploaded_file_' + i];
        }
      }
    }
  },
};
</script>
